import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type AssigneesSectionLazyFragment, EditIssueAssigneesSection} from '@github-ui/issue-metadata/AssigneesSection'
import {EditIssueFieldsSection} from '@github-ui/issue-metadata/FieldsSection'
import {EditIssueLabelsSection} from '@github-ui/issue-metadata/LabelsSection'
import {EditIssueMilestonesSection} from '@github-ui/issue-metadata/MilestonesSection'
import {EditIssueProjectsSection, ProjectsSectionFallback} from '@github-ui/issue-metadata/ProjectsSection'
import {EditIssueIssueTypeSection} from '@github-ui/issue-metadata/TypesSection'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {StickySidebar} from '@github-ui/sticky-sidebar'
import {clsx} from 'clsx'
import {Suspense, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {IssueSidebarPrimaryQuery$key} from './__generated__/IssueSidebarPrimaryQuery.graphql'
import type {IssueViewerSecondaryIssueData$data} from './__generated__/IssueViewerSecondaryIssueData.graphql'
import type {IssueViewerViewer$data} from './__generated__/IssueViewerViewer.graphql'
import {FIXED_HEADER_HEIGHT} from './header/constants/sticky-elements'
import type {IssueSecondaryNode} from './issue-viewer-secondary-query-types'
import styles from './IssueSidebar.module.css'
import {
  IssueSidebarLazySections,
  IssueSidebarLazySectionsFallback,
  type IssueSidebarLazySectionsFragment,
} from './IssueSidebarLazySections'
import type {OptionConfig} from './OptionConfig'
import {OptionsSection} from './sections/OptionsSection'

type IssueSidebarSecondaryRepository = Pick<
  NonNullable<IssueViewerSecondaryIssueData$data['repository']>,
  'isLocked' | 'viewerCanPush'
>
type IssueSidebarSecondarySuggestedActors = Pick<IssueViewerSecondaryIssueData$data, 'suggestedActors'>
type IssueSidebarSecondaryFragment = RelayCompat<
  Pick<IssueSecondaryNode, 'isTransferInProgress'> &
    IssueSidebarSecondarySuggestedActors & {
      repository: IssueSidebarSecondaryRepository
    }
>

// RELAY-CLEANUP(IssueViewerSecondaryIssueDataFragment): the secondary issue data is still a Relay
// fragment ref. Remove the RelayCompat widening when IssueViewerSecondaryIssueDataFragment leaves Relay.
export type IssueSidebarSecondaryKey = IssueSidebarSecondaryFragment

type IssueSidebarProps = {
  sidebarKey: IssueSidebarPrimaryQuery$key
  sidebarSecondaryKey?: IssueSidebarSecondaryKey
  lazySecondaryData?: IssueSidebarLazySectionsFragment
  optionConfig: OptionConfig
  viewer: IssueViewerViewer$data | null
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueSidebarPrimaryGraphqlQuery = graphql`
  fragment IssueSidebarPrimaryQuery on Issue @argumentDefinitions(allowedOwner: {type: "String", defaultValue: null}) {
    ...AssigneesSectionFragment
    ...LabelsSectionFragment
    ...ProjectsSectionFragment @arguments(allowedOwner: $allowedOwner)
    ...MilestonesSectionFragment
    ...OptionsSectionFragment
    ...TypesSectionFragment
    ...FieldsSectionFragment
  }
`

export function IssueSidebar({
  sidebarKey,
  sidebarSecondaryKey: issueSecondary,
  lazySecondaryData,
  optionConfig,
  viewer,
}: IssueSidebarProps) {
  const issue = useFragment(IssueSidebarPrimaryGraphqlQuery, sidebarKey)
  const modernUx = isFeatureEnabled('issues_modern_ux')

  const sidebar = useMemo(() => {
    const viewerCanPush = issueSecondary?.repository?.viewerCanPush ?? false
    const isRepoLocked = issueSecondary?.repository?.isLocked ?? false
    const readonly = optionConfig.isReadonly || isRepoLocked

    return (
      <div
        className={clsx(styles.sidebarContent, modernUx && styles.sidebarContentModernUx)}
        data-modern-ux={modernUx || undefined}
      >
        <EditIssueAssigneesSection
          issue={issue}
          viewer={viewer}
          // RELAY-CLEANUP(IssueViewerSecondaryViewQuery): the still-Relay secondary query shape for
          // `suggestedActors` is wider than the migrated plain-props type. Remove when
          // IssueViewerSecondaryViewQuery leaves Relay.
          lazyKey={(issueSecondary ?? undefined) as AssigneesSectionLazyFragment}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <EditIssueLabelsSection
          issue={issue}
          canCreateLabel={viewerCanPush}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <EditIssueIssueTypeSection
          issue={issue}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <EditIssueFieldsSection
          issue={issue}
          onUpdate={optionConfig.onIssueUpdate}
          inProjectsPage={!!optionConfig.selectedProjectId}
          readonly={readonly}
        />
        <Suspense fallback={<ProjectsSectionFallback />}>
          <ErrorBoundary fallback={<ProjectsSectionFallback />}>
            <EditIssueProjectsSection
              selectedProjectId={optionConfig.selectedProjectId}
              allowedProjectOwner={optionConfig.allowedProjectOwner}
              issueOrPullRequest={issue}
              onIssueUpdate={optionConfig.onIssueUpdate}
              onRemovedFromProject={optionConfig.onRemovedFromProject}
              insideSidePanel={optionConfig.insideSidePanel}
              readonly={readonly}
            />
          </ErrorBoundary>
        </Suspense>
        <EditIssueMilestonesSection
          issue={issue}
          canCreateMilestone={viewerCanPush}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <Suspense fallback={<IssueSidebarLazySectionsFallback />}>
          <IssueSidebarLazySections
            onLinkClick={optionConfig.onLinkClick}
            onIssueUpdate={optionConfig.onIssueUpdate}
            insideSidePanel={optionConfig.insideSidePanel}
            issueData={lazySecondaryData}
            viewer={viewer}
            readonly={readonly}
          />
        </Suspense>
        {!readonly && (
          <OptionsSection issue={issue} optionsSectionSecondary={issueSecondary} optionConfig={optionConfig} />
        )}
      </div>
    )
  }, [issue, issueSecondary, lazySecondaryData, optionConfig, viewer, modernUx])

  return <StickySidebar topOffset={FIXED_HEADER_HEIGHT}>{sidebar}</StickySidebar>
}
